var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
// src/index.ts
import { ZodIssueCode, ZodParsedType, defaultErrorMap } from "zod";
import i18next from "i18next";
var jsonStringifyReplacer = function (_, value) {
    if (typeof value === "bigint") {
        return value.toString();
    }
    return value;
};
function joinValues(array, separator) {
    if (separator === void 0) { separator = " | "; }
    return array.map(function (val) { return typeof val === "string" ? "'".concat(val, "'") : val; }).join(separator);
}
var isRecord = function (value) {
    if (typeof value !== "object" || value === null)
        return false;
    for (var key in value) {
        if (!Object.prototype.hasOwnProperty.call(value, key))
            return false;
    }
    return true;
};
var getKeyAndValues = function (param, defaultKey) {
    if (typeof param === "string")
        return { key: param, values: {} };
    if (isRecord(param)) {
        var key = "key" in param && typeof param.key === "string" ? param.key : defaultKey;
        var values = "values" in param && isRecord(param.values) ? param.values : {};
        return { key: key, values: values };
    }
    return { key: defaultKey, values: {} };
};
var defaultNs = "zod";
var makeZodI18nMap = function (option) { return function (issue, ctx) {
    var _a, _b;
    var _c = __assign(__assign({ t: i18next.t, ns: defaultNs }, option), { handlePath: (option === null || option === void 0 ? void 0 : option.handlePath) !== false ? __assign({ context: "with_path", ns: (_a = option === null || option === void 0 ? void 0 : option.ns) !== null && _a !== void 0 ? _a : defaultNs, keyPrefix: void 0 }, option === null || option === void 0 ? void 0 : option.handlePath) : null }), t = _c.t, ns = _c.ns, handlePath = _c.handlePath;
    var message;
    message = defaultErrorMap(issue, ctx).message;
    var path = issue.path.length > 0 && !!handlePath ? {
        context: handlePath.context,
        path: t([handlePath.keyPrefix, issue.path.join(".")].filter(Boolean).join("."), {
            ns: handlePath.ns,
            defaultValue: issue.path.join(".")
        })
    } : {};
    switch (issue.code) {
        case ZodIssueCode.invalid_type:
            if (issue.received === ZodParsedType.undefined) {
                message = t("errors.invalid_type_received_undefined", __assign({ ns: ns, defaultValue: message }, path));
            }
            else if (issue.received === ZodParsedType.null) {
                message = t("errors.invalid_type_received_null", __assign({ ns: ns, defaultValue: message }, path));
            }
            else {
                message = t("errors.invalid_type", __assign({ expected: t("types.".concat(issue.expected), {
                        defaultValue: issue.expected,
                        ns: ns
                    }), received: t("types.".concat(issue.received), {
                        defaultValue: issue.received,
                        ns: ns
                    }), ns: ns, defaultValue: message }, path));
            }
            break;
        case ZodIssueCode.invalid_literal:
            message = t("errors.invalid_literal", __assign({ expected: JSON.stringify(issue.expected, jsonStringifyReplacer), ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.unrecognized_keys:
            message = t("errors.unrecognized_keys", __assign({ keys: joinValues(issue.keys, ", "), count: issue.keys.length, ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.invalid_union:
            message = t("errors.invalid_union", __assign({ ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.invalid_union_discriminator:
            message = t("errors.invalid_union_discriminator", __assign({ options: joinValues(issue.options), ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.invalid_enum_value:
            message = t("errors.invalid_enum_value", __assign({ options: joinValues(issue.options), received: issue.received, ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.invalid_arguments:
            message = t("errors.invalid_arguments", __assign({ ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.invalid_return_type:
            message = t("errors.invalid_return_type", __assign({ ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.invalid_date:
            message = t("errors.invalid_date", __assign({ ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.invalid_string:
            if (typeof issue.validation === "object") {
                if ("startsWith" in issue.validation) {
                    message = t("errors.invalid_string.startsWith", __assign({ startsWith: issue.validation.startsWith, ns: ns, defaultValue: message }, path));
                }
                else if ("endsWith" in issue.validation) {
                    message = t("errors.invalid_string.endsWith", __assign({ endsWith: issue.validation.endsWith, ns: ns, defaultValue: message }, path));
                }
            }
            else {
                message = t("errors.invalid_string.".concat(issue.validation), __assign({ validation: t("validations.".concat(issue.validation), {
                        defaultValue: issue.validation,
                        ns: ns
                    }), ns: ns, defaultValue: message }, path));
            }
            break;
        case ZodIssueCode.too_small:
            var minimum = issue.type === "date" ? new Date(issue.minimum) : issue.minimum;
            message = t("errors.too_small.".concat(issue.type, ".").concat(issue.exact ? "exact" : issue.inclusive ? "inclusive" : "not_inclusive"), __assign({ minimum: minimum, count: typeof minimum === "number" ? minimum : void 0, ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.too_big:
            var maximum = issue.type === "date" ? new Date(issue.maximum) : issue.maximum;
            message = t("errors.too_big.".concat(issue.type, ".").concat(issue.exact ? "exact" : issue.inclusive ? "inclusive" : "not_inclusive"), __assign({ maximum: maximum, count: typeof maximum === "number" ? maximum : void 0, ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.custom:
            var _d = getKeyAndValues((_b = issue.params) === null || _b === void 0 ? void 0 : _b.i18n, "errors.custom"), key = _d.key, values = _d.values;
            message = t(key, __assign(__assign(__assign({}, values), { ns: ns, defaultValue: message }), path));
            break;
        case ZodIssueCode.invalid_intersection_types:
            message = t("errors.invalid_intersection_types", __assign({ ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.not_multiple_of:
            message = t("errors.not_multiple_of", __assign({ multipleOf: issue.multipleOf, ns: ns, defaultValue: message }, path));
            break;
        case ZodIssueCode.not_finite:
            message = t("errors.not_finite", __assign({ ns: ns, defaultValue: message }, path));
            break;
        default:
    }
    return { message: message };
}; };
var zodI18nMap = makeZodI18nMap();
export { makeZodI18nMap, zodI18nMap };
//# sourceMappingURL=index.mjs.map
